/**
 * /admin/import — Excel/CSV import UI (BUILD_SPEC.md §11.1, §10.6).
 *
 * Mirrors the two-phase server flow: upload → preview → commit. The
 * preview phase shows the auto-detected column mapping and a five-row
 * sample; admins can override any column before confirming the
 * commit. A summary modal at the end breaks the results into
 * imported / skipped / errors counts with each error expandable to
 * its row + message.
 *
 * The page is intentionally a single-component file — it's a
 * single-purpose admin workflow and breaking it into smaller files
 * would obscure the linear preview → commit story.
 */

import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { useProgressStore } from "@/stores/progressStore";
import { AxiosError } from "axios";
import { UploadCloud, AlertTriangle, CheckCircle2, X, FileSpreadsheet } from "lucide-react";
import {
  previewImport,
  commitImport,
  type ColumnMapping,
  type ImportField,
  type PreviewResponse,
  type CommitResponse,
} from "@/api/import";

/** Seven importable fields, in spec order. Used to drive the
 *  mapping-dropdown matrix below. */
const FIELDS: ImportField[] = [
  "requestNumber",
  "year",
  "region",
  "serviceType",
  "followUpPhone",
  "ownerName",
  "notes",
];

const REQUIRED_FIELDS = new Set<ImportField>([
  "requestNumber",
  "year",
  "region",
  "serviceType",
  "followUpPhone",
]);

/** Excel-style column letter for a given 0-based index. Mirrors the
 *  server-side helper but inlined here so the SPA doesn't have to
 *  depend on the API's lib module. */
function columnLetter(idx: number): string {
  let n = idx;
  let s = "";
  while (n >= 0) {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  }
  return s;
}

interface ApiErrorBody {
  error?: { code?: string; message?: string };
}

/** Extracts the human-friendly message from an axios error, falling
 *  back to a generic message when the response doesn't follow the
 *  canonical envelope. */
function errorMessage(err: unknown): string {
  if (err instanceof AxiosError) {
    const body = err.response?.data as ApiErrorBody | undefined;
    return body?.error?.message ?? err.message;
  }
  return (err as Error).message;
}

export function ImportAdminPage() {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping | null>(null);
  const [result, setResult] = useState<CommitResponse | null>(null);
  const progressStart = useProgressStore((s) => s.start);
  const progressEnd = useProgressStore((s) => s.end);

  const previewMutation = useMutation({
    mutationFn: async (file: File) => {
      progressStart();
      return previewImport(file);
    },
    onSuccess: (data) => {
      setPreview(data);
      setMapping(data.detectedColumns);
      setResult(null);
    },
    onSettled: () => progressEnd(),
  });

  const commitMutation = useMutation({
    mutationFn: async () => {
      if (!preview || !mapping) throw new Error("No preview to commit");
      progressStart();
      return commitImport(preview.fileId, mapping);
    },
    onSuccess: (data) => {
      setResult(data);
      // Clear the preview state so a follow-up file pick starts
      // clean; the result modal remains visible until dismissed.
      setPreview(null);
      setMapping(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    onSettled: () => progressEnd(),
  });

  const onFile = (file: File | undefined) => {
    if (!file) return;
    previewMutation.mutate(file);
  };

  /** Available column letters for the dropdowns — derived from the
   *  preview's header row width so we never offer a column the file
   *  doesn't have. */
  const columnOptions = useMemo(() => {
    if (!preview) return [];
    return preview.headerRow.map((header, i) => ({
      letter: columnLetter(i),
      header: header || `(${t("import.unnamedColumn")})`,
    }));
  }, [preview, t]);

  const requiredUnmapped = useMemo(() => {
    if (!mapping) return [] as ImportField[];
    return FIELDS.filter((f) => REQUIRED_FIELDS.has(f) && !mapping[f]);
  }, [mapping]);

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-fg dark:text-fg-dark">{t("import.title")}</h2>

      {/* Upload card */}
      <div className="rounded-xl border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark p-6">
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.csv"
          onChange={(e) => onFile(e.target.files?.[0])}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={previewMutation.isPending}
          className="flex items-center gap-2 rounded-md bg-fg dark:bg-fg-dark text-canvas dark:text-canvas-dark px-4 py-2 text-sm font-semibold disabled:opacity-50"
        >
          <UploadCloud className="h-4 w-4" aria-hidden />
          {previewMutation.isPending ? t("import.parsing") : t("import.chooseFile")}
        </button>
        <p className="mt-2 text-xs text-muted dark:text-muted-dark">{t("import.fileHint")}</p>
        {previewMutation.error && (
          <p className="mt-3 rounded-md bg-rejection-bg dark:bg-rejection-dark-bg/40 p-2 text-sm text-rejection dark:text-rejection-dark">
            {errorMessage(previewMutation.error)}
          </p>
        )}
      </div>

      {/* Preview + column mapping */}
      {preview && mapping && (
        <div className="rounded-xl border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark p-4 space-y-4">
          <div className="flex items-center gap-2 text-sm text-fg dark:text-fg-dark">
            <FileSpreadsheet className="h-4 w-4" aria-hidden />
            <span className="font-semibold">{preview.fileName}</span>
            <span className="text-muted dark:text-muted-dark">
              · {t("import.totalRows", { count: preview.totalRows })}
            </span>
          </div>

          {preview.warnings.length > 0 && (
            <div className="rounded-md bg-assignment-bg dark:bg-assignment-dark-bg/40 p-3 text-sm flex items-start gap-2">
              <AlertTriangle
                className="h-4 w-4 mt-0.5 text-assignment dark:text-assignment-dark shrink-0"
                aria-hidden
              />
              <ul className="space-y-1">
                {preview.warnings.map((w) => (
                  <li key={w} className="text-fg dark:text-fg-dark">
                    {w}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <h3 className="mb-2 text-sm font-semibold text-fg dark:text-fg-dark">
              {t("import.mapColumns")}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {FIELDS.map((field) => {
                const isRequired = REQUIRED_FIELDS.has(field);
                return (
                  <label key={field} className="flex items-center gap-2 text-sm">
                    <span className="w-40 text-fg dark:text-fg-dark">
                      {t(`import.field.${field}`)}
                      {isRequired ? (
                        <span className="text-rejection dark:text-rejection-dark ms-1">*</span>
                      ) : null}
                    </span>
                    <select
                      value={mapping[field] ?? ""}
                      onChange={(e) =>
                        setMapping({
                          ...mapping,
                          [field]: e.target.value === "" ? null : e.target.value,
                        })
                      }
                      className="rounded-md border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark px-2 py-1 text-sm text-fg dark:text-fg-dark"
                    >
                      <option value="">— {t("import.pickColumn")} —</option>
                      {columnOptions.map((c) => (
                        <option key={c.letter} value={c.letter}>
                          {c.letter} · {c.header}
                        </option>
                      ))}
                    </select>
                  </label>
                );
              })}
            </div>
            {requiredUnmapped.length > 0 && (
              <p className="mt-2 text-xs text-rejection dark:text-rejection-dark">
                {t("import.requiredHint", { count: requiredUnmapped.length })}
              </p>
            )}
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold text-fg dark:text-fg-dark">
              {t("import.firstFive")}
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-hairline/40 dark:bg-hairline-dark/60">
                    {preview.headerRow.map((h, i) => (
                      <th
                        key={i}
                        className="px-2 py-1.5 text-start font-semibold text-fg dark:text-fg-dark"
                      >
                        {columnLetter(i)} · {h || "—"}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.firstFiveRows.map((row, ri) => (
                    <tr
                      key={ri}
                      className="border-t border-hairline dark:border-hairline-dark"
                    >
                      {row.map((cell, ci) => (
                        <td
                          key={ci}
                          className="rtl-isolate px-2 py-1.5 text-fg dark:text-fg-dark"
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {commitMutation.error && (
            <p className="rounded-md bg-rejection-bg dark:bg-rejection-dark-bg/40 p-2 text-sm text-rejection dark:text-rejection-dark">
              {errorMessage(commitMutation.error)}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setPreview(null);
                setMapping(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
              className="rounded-md border border-hairline dark:border-hairline-dark px-4 py-2 text-sm text-fg dark:text-fg-dark"
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={() => commitMutation.mutate()}
              disabled={commitMutation.isPending || requiredUnmapped.length > 0}
              className="rounded-md bg-fg dark:bg-fg-dark text-canvas dark:text-canvas-dark px-4 py-2 text-sm font-semibold disabled:opacity-50"
            >
              {commitMutation.isPending ? t("import.committing") : t("import.commit")}
            </button>
          </div>
        </div>
      )}

      {/* Result modal */}
      {result && <ResultModal result={result} onClose={() => setResult(null)} />}
    </div>
  );
}

interface ResultModalProps {
  result: CommitResponse;
  onClose: () => void;
}

function ResultModal({ result, onClose }: ResultModalProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="max-w-lg w-full rounded-xl bg-surface dark:bg-surface-dark border border-hairline dark:border-hairline-dark p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-lg font-bold text-fg dark:text-fg-dark flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-approval dark:text-approval-dark" aria-hidden />
            {t("import.resultTitle")}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.cancel")}
            className="rounded-md p-1 text-muted dark:text-muted-dark hover:bg-hairline/40 dark:hover:bg-hairline-dark/40"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
        <dl className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-md bg-approval-bg dark:bg-approval-dark-bg/40 p-3">
            <dt className="text-xs text-muted dark:text-muted-dark">
              {t("import.imported")}
            </dt>
            <dd className="text-2xl font-bold text-approval dark:text-approval-dark">
              {result.imported}
            </dd>
          </div>
          <div className="rounded-md bg-hairline/40 dark:bg-hairline-dark/60 p-3">
            <dt className="text-xs text-muted dark:text-muted-dark">{t("import.skipped")}</dt>
            <dd className="text-2xl font-bold text-fg dark:text-fg-dark">{result.skipped}</dd>
          </div>
          <div className="rounded-md bg-rejection-bg dark:bg-rejection-dark-bg/40 p-3">
            <dt className="text-xs text-muted dark:text-muted-dark">{t("import.errors")}</dt>
            <dd className="text-2xl font-bold text-rejection dark:text-rejection-dark">
              {result.errors.length}
            </dd>
          </div>
        </dl>
        {result.errors.length > 0 && (
          <div className="mt-4">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-sm text-pulse dark:text-pulse-dark underline"
            >
              {expanded ? t("import.hideErrors") : t("import.showErrors")}
            </button>
            {expanded && (
              <ul className="mt-2 max-h-64 overflow-y-auto text-xs space-y-1">
                {result.errors.map((e) => (
                  <li
                    key={`${e.row}-${e.message}`}
                    className="border-b border-hairline dark:border-hairline-dark py-1 text-fg dark:text-fg-dark"
                  >
                    <span className="font-semibold">
                      {t("import.row")} {e.row}:
                    </span>{" "}
                    {e.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
